/* eslint-disable custom-rules/modal-and-drawer-requires-title */
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { tokenizer } from '@uhg-abyss/web/tools/tokenizer';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { emailRegex } from '../../frontends/ProviderSearch/routes/MapView/ShareDrawer/utils';
import { useLagoon } from '../../hooks/useLagoon';
import { useSendEmail } from '../../hooks/useSendEmail';
import { ProviderLocation, ProviderType } from '../../models/ProviderDetails';
import { useDetailsStore } from '../../store/useDetailsStore';
import { DetailsStore } from '../../store/useDetailsStore/detailsStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { capitalizeEachWord } from '../../utils/providerNames.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { Constants, ProviderTypes } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ConstantsRoutes } from '../ConstantsRoutes';
import { phoneOnly } from '../ConstantsStyles';
import { getCurrentDateWithDashFormatting } from '../Utils/datesUtils';
import {
  ShareThisProviderButton,
  ShareThisProviderButtonWrapper,
  ShareThisProviderHeadingName,
  ShareThisProviderHeadingWrapper,
  ShareThisProviderRoot,
  ShareThisProviderText,
  ShareThisProviderTextInput,
  ShareThisProviderTextInputURL,
  toastSuccessStyle,
} from './ShareThisProvider.style';

type Props = {
  showShareThisProviderDrawer: boolean;
  setShowShareThisProviderDrawer: Function;
  providerName: string;
  providerType: ProviderType;
  speciality: string[];
  professionalDesignation?: string;
  providerLocations: ProviderLocation[];
};

export const ShareThisProviderDrawer = ({
  showShareThisProviderDrawer,
  setShowShareThisProviderDrawer,
  providerName,
  providerType,
  speciality,
  professionalDesignation,
  providerLocations,
}: Props) => {
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(phoneOnly);
  const [, sendEmail] = useSendEmail({});
  const { toast } = useToast();
  const sendEmailTestId = 'send-email';
  const { sectionType } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      sectionType: state.typeaheadSearchStore.sectionType,
    }))
  );

  const { providerId } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      providerId: state.detailsState.providerId,
    }))
  );

  const updatedToken = tokenizer.create({
    providerId,
    sectionType,
  });

  const detailsRoute =
    sectionType === Constants.RESULT_SECTION.FACILITY_CAPITAL
      ? ConstantsRoutes.FACILITY_DETAILS.path
      : ConstantsRoutes.PROVIDER_DETAILS.path;

  const shareUrl = `${window.location.origin}/findcare${detailsRoute}${updatedToken}`;
  const data = useLagoon('ui-messaging')();
  const modalContent = find(data, {
    key: ConstantsLagoon.SHARE_THIS_PROVIDER.SHARE_THIS_PROVIDER_MESSAGE,
  });
  const form = useForm();

  const validEmail = find(data, {
    key: ConstantsLagoon.VALID_EMAIL_ADDRESS.VALID_EMAIL_ADDRESS_MESSAGE,
  })?.message;

  const handleDrawerClose = () => {
    adobeLinkTrackEvent({
      name: 'cancel',
      location: 'modal:share-this-provider',
      type: 'internal',
    });
    form.reset();
    setShowShareThisProviderDrawer(false);
  };
  const formatEmails = (): string[] => {
    const formValues: string[] = Object.values(form.getValues());
    return formValues.filter((val: string) => val.length);
  };

  const handleDrawerSubmit = async () => {
    const subjectText: string = (() => {
      switch (providerType?.toLowerCase()) {
        case ProviderTypes.PRACTITIONER.toLowerCase():
          return t('SHARE_THIS_PROVIDER.PROVIDER');
        case ProviderTypes.ORGANIZATION.toLowerCase():
          return t('SHARE_THIS_PROVIDER.FACILITY');
        default:
          return t('SHARE_THIS_PROVIDER.PROVIDER_GROUP');
      }
    })();

    adobeLinkTrackEvent({
      name: sendEmailTestId,
      location: 'modal:share-this-provider',
      type: 'internal',
    });
    let text: string = `<div><div><b>${subjectText} ${providerName} ${t(
      'SHARE_THIS_PROVIDER.ON'
    )} ${getCurrentDateWithDashFormatting()}</b><br><br>${providerName}${
      professionalDesignation ? `, ${professionalDesignation}` : ''
    }${speciality ? `${speciality}` : ''}<br>
    </div>`;

    providerLocations.forEach((provider, index) => {
      text += `<div style="margin-top:10px"><br>Location ${index + 1}:<br>${
        provider.address.line[0]
      }<br>${provider.address?.city}, ${
        provider.address?.state
      }, ${provider.address?.postalCode.slice(0, 5)}<br>${
        provider.phones?.phone?.[0] || ''
      }</div>`;
    });
    text += `<div style="margin-top:10px">[${shareUrl}]<div>`;
    if (formatEmails().length) {
      const response = await sendEmail({
        variables: {
          recipientEmails: formatEmails(),
          subject: `${subjectText} ${providerName} ${t(
            'SHARE_THIS_PROVIDER.ON'
          )} ${getCurrentDateWithDashFormatting()}`,
          text: '',
          htmlText: text,
        },
      });
      if (response.status === 200) {
        toast.show({
          message: (
            <Text color="white">
              {t('TOAST.YOUR_RESULTS_HAVE_BEEN_SHARED')}
            </Text>
          ),
          type: 'success',
          icon: <IconSymbol color="white !important" icon="check_circle" />,
          css: toastSuccessStyle,
        });
        form.reset();
        setShowShareThisProviderDrawer(false);
      }
    }
  };

  return (
    <Drawer
      css={{
        'abyss-modal-close-button': {
          top: '25px',
        },
      }}
      header={
        <Heading
          color="$neutralGray7"
          css={{
            'abyss-heading-root': {
              fontSize: '25.63px !important',
              lineHeight: '32px !important',
            },
          }}
          data-auto-testid="share-this-provider-heading"
          data-testid="share-this-provider-heading"
          level={4}
          size="sm"
        >
          {t('SHARE_THIS_PROVIDER.SHARE_THIS_PROVIDER')}
        </Heading>
      }
      isOpen={showShareThisProviderDrawer}
      onClose={handleDrawerClose}
      position="right"
      size={mobileScreen ? window.innerHeight - 104 : 408}
    >
      <FormProvider
        data-auto-testid="suggest-edit-form"
        data-testid="suggest-edit-form"
        state={form}
      >
        <ShareThisProviderRoot direction="column">
          <ShareThisProviderHeadingWrapper
            alignItems="flex-start"
            direction="column"
          >
            <ShareThisProviderHeadingName color="$neutralGray7" level={6}>
              {capitalizeEachWord(providerName)}
              {professionalDesignation && `, ${professionalDesignation}`}
            </ShareThisProviderHeadingName>
            <ShareThisProviderText color="$core.color.neutral.80">
              {capitalizeEachWord(speciality?.[0])}
            </ShareThisProviderText>
            <ShareThisProviderTextInputURL
              data-auto-testid="share-url"
              data-testid="share-url"
              hideLabel
              value={shareUrl}
            />
          </ShareThisProviderHeadingWrapper>
          <ShareThisProviderText color="$neutralGray">
            {t('SHARE_THIS_PROVIDER.LOCATIONS_SHARE_TEXT')}
          </ShareThisProviderText>
          <ShareThisProviderTextInput
            data-auto-testid="email-address"
            data-testid="email-address"
            label={t('SHARE_THIS_PROVIDER.EMAIL_ADDRESS')}
            model="email"
            onBlur={() => {
              form.trigger('email');
            }}
            validators={{
              required: true,
              pattern: {
                value: emailRegex,
                message: validEmail,
              },
            }}
          />

          {[...Array(4)].map((element, i) => (
            <ShareThisProviderTextInput
              data-auto-testid={`optional-email-address-${i}`}
              data-testid={`optional-email-address-${i}`}
              hideLabel={i > 0}
              key={i}
              label={
                i === 0 ? t('SHARE_THIS_PROVIDER.OPTIONAL_EMAIL_ADDRESS') : ''
              }
              model={`optionalEmail${i}`}
              onBlur={() => {
                form.trigger(`optionalEmail${i}`);
              }}
              validators={{
                required: false,
                // eslint-disable-next-line consistent-return
                validate: (v) => {
                  if (v && !emailRegex.test(v)) {
                    return 'Please input a valid email address';
                  }
                },
              }}
            />
          ))}
          <ShareThisProviderButtonWrapper
            alignItems="flex-start"
            direction="row"
            justify="flex-end"
          >
            <ShareThisProviderButton
              data-auto-testid="cancel"
              data-testid="cancel"
              onClick={handleDrawerClose}
              size="$md"
              type="cancel"
              variant="outline"
            >
              {t('SHARE_THIS_PROVIDER.CANCEL')}
            </ShareThisProviderButton>
            <ShareThisProviderButton
              data-auto-testid={sendEmailTestId}
              data-testid={sendEmailTestId}
              onClick={handleDrawerSubmit}
              size="$md"
              type="submit"
            >
              {t('SHARE_THIS_PROVIDER.SEND_EMAIL')}
            </ShareThisProviderButton>
          </ShareThisProviderButtonWrapper>
          <ShareThisProviderText
            color="$neutralGray"
            data-auto-testid="provider-text"
            data-testid="provider-text"
          >
            {modalContent?.message}
          </ShareThisProviderText>
        </ShareThisProviderRoot>
      </FormProvider>
    </Drawer>
  );
};
